import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import { Navigate } from "react-router-dom";
import PageLoading from "src/components/pageLoading";
import { useCountLoungeRequestsQuery, useGetLoungeRequestsQuery } from "../lounge.service";
import { Lounge } from "../types/lounge.entity";
import { LoungeSearch } from "../components/lounges/search";
import { LoungeTable } from "../components/lounges/table";
import { LoungeTableSkeleton } from "../components/lounges/LoungeTableSkeleton";
import { LoungeFilterInput } from "../types";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

type LoungeRouteState = {
  id?: string;
  user?: string;
  language?: string;
  status?: Lounge["status"];
  currency?: Lounge["currency"];
};

export const LoungeRequests = () => {
  const tableSearch = usePaginatedTableSearchParams<LoungeRouteState>({
    parseState: (searchParams) => {
      const query: LoungeRouteState = {};
      const id = searchParams.get("id");
      const user = searchParams.get("user");
      const language = searchParams.get("language");
      const status = searchParams.get("status");
      const currency = searchParams.get("currency");

      if (id) query.id = id;
      if (user) query.user = user;
      if (language) query.language = language;
      if (status) query.status = status as Lounge["status"];
      if (currency) query.currency = currency as Lounge["currency"];

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.id) nextParams.set("id", state.id);
      if (state.user) nextParams.set("user", state.user);
      if (state.language) nextParams.set("language", state.language);
      if (state.status) nextParams.set("status", state.status);
      if (state.currency) nextParams.set("currency", state.currency);
      return nextParams;
    },
  });

  const initialQuery: LoungeFilterInput = {
    ...(tableSearch.state.id ? { id: tableSearch.state.id } : {}),
    ...(tableSearch.state.user ? { userIdentifier: tableSearch.state.user } : {}),
    ...(tableSearch.state.language ? { langCode: tableSearch.state.language } : {}),
    ...(tableSearch.state.status ? { status: tableSearch.state.status } : {}),
    ...(tableSearch.state.currency ? { currency: tableSearch.state.currency } : {}),
  };

  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } = usePagination<Lounge>({
    useGetDataQuery: useGetLoungeRequestsQuery,
    useCountData: useCountLoungeRequestsQuery,
    initQuery: initialQuery,
    initPageTokens: tableSearch.pageTokens,
    initCurrentIndex: tableSearch.currentIndex,
    syncKey: tableSearch.syncKey,
    onPaginationStateChange: tableSearch.replacePagination,
  });

  const showTableSkeleton = !data && (isLoading || isFetching);

  return (
    <div className="page_container">
      <PageHeader text="Lounge Requests" />

      <LoungeSearch updateFilterQuery={tableSearch.replaceFilters} initialQuery={tableSearch.state} />

      {isSuccess ? (
        <>
          {showTableSkeleton ? (
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
                <thead className="bg-gray-800 text-white text-center">
                  <tr>
                    <th className="px-4 py-3">Id</th>
                    <th className="px-4 py-3">Language</th>
                    <th className="px-4 py-3">Search Amount</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created At</th>
                    <th className="px-4 py-3">View</th>
                  </tr>
                </thead>
                <tbody>
                  <LoungeTableSkeleton rows={10} />
                </tbody>
              </table>
            </div>
          ) : (
            <LoungeTable data={data ?? []} />
          )}
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <Navigate to="/505" />
      ) : (
        <PageLoading />
      )}
    </div>
  );
};
